'use client'

import Trash from 'public/assets/trash.svg'
import Upload from 'public/assets/upload.svg'
import { useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'
import { formatBytes } from 'utils/formatHelpers'
import styles from './DropzoneInput.module.scss'

type Props = UseControllerProps<FieldValues> & {
  multiple: boolean
  maxFiles?: number
}

const DropzoneInput = ({ name, multiple, maxFiles = 1, control }: Props) => {
  const { field } = useController({ name, control })
  const [files, setFiles] = useState<File[]>([])

  const onDrop = <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      return
    }
    let newFiles: File[] = acceptedFiles.filter((af) => !files.some((f) => f.name === af.name))
    const newLength = files.length + acceptedFiles.length
    if (newLength > maxFiles) {
      newFiles = [...files.slice(newLength - maxFiles), ...newFiles]
    } else {
      newFiles = [...files, ...newFiles]
    }
    setFiles(newFiles)
    field.onChange(newFiles)
  }
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/pdf': ['.pdf'],
    },
    maxFiles,
    multiple,
  })

  const removeFile = (file: File) => {
    const newFiles = files.filter((f) => f.name !== file.name)
    setFiles(newFiles)
    field.onChange(newFiles)
  }

  return (
    <div className={styles.container}>
      <div className={styles.dropzone} {...getRootProps()}>
        <Upload />
        <label htmlFor={name}>Browse or drag and drop your CV, portfolio, and any other relevant documents</label>
      </div>
      <input name={name} id={name} {...getInputProps()} />

      {files.length > 0 && (
        <div className={styles.files}>
          {files.map((file, index) => (
            <div className={styles.file} key={index}>
              <span>{file.name}</span>
              <div className={styles['file-size']}>
                <span className={styles.size}>{formatBytes(file.size)}</span>
                <Trash onClick={() => removeFile(file)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default DropzoneInput
